import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Header } from '../components/layout/Header';
import { ApiKeyManager } from '../components/settings/ApiKeyManager';
import { ProfileSettings } from '../components/settings/ProfileSettings';
import { PasswordReset } from '../components/settings/PasswordReset';
import { usePageTitle } from '../hooks/usePageTitle';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';

/**
 * Settings page
 * Manages user preferences and API keys
 */
export const Settings: React.FC = () => {
  usePageTitle('Settings');
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'api-keys' | 'account'>('api-keys');

  if (!user) {
    return null;
  }

  // Check if user has password provider (email/password authentication)
  const isPasswordProvider = user.providerData.some(
    (provider) => provider.providerId === 'password'
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EFE4D2' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Settings' },
          ]}
        />

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage your account preferences and API keys
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('api-keys')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'api-keys'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  API Keys
                </div>
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'account'
                    ? 'border-primary-500 text-primary-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Account
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'api-keys' && <ApiKeyManager />}
            {activeTab === 'account' && (
              <div className="space-y-8">
                <ProfileSettings userId={user.uid} userEmail={user.email || ''} />
                <PasswordReset userEmail={user.email || ''} isPasswordProvider={isPasswordProvider} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
